// src/pages/WarrantiesPage.jsx
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Box,
    Typography,
    Paper,
    TextField,
    Autocomplete,
    Divider,
    Button,
    Chip,
    Stack,
    Switch,
    FormControlLabel,
    InputAdornment,
} from "@mui/material";
import Alert from "@mui/material/Alert";

import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { selectSellers } from "../../store/sellers/sellers.selector";
import { tokens } from "../../theme";

import SearchIcon from "@mui/icons-material/Search";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

import { djangoApi } from "../../utils/axios/axios.utils";

// 🔹 COMPONENTE
import WarrantyProductCard from "../../components/warranties/WarrantyProductCard";
import WarrantyCatalogByCategory from "../../components/warranties/WarrantyCatalogByCategory";

// 🔹 UTILS (já existentes)
import {
    findBestMatchByCode,
    fmtBRL,
} from "../../components/warranties/warranties.utils";

import WarrantiesSection from "../../components/warranties/WarrantiesSection";

const EXAMPLE_SELLER = {};

const WarrantiesPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const sellersFromStore = useSelector(selectSellers);
    const SELLERS = useMemo(() => {
        const list = Array.isArray(sellersFromStore) ? sellersFromStore : [];
        return list.length ? list : [EXAMPLE_SELLER];
    }, [sellersFromStore]);

    const [seller, setSeller] = useState(null);

    const [loadedProducts, setLoadedProducts] = useState([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);

    const [productCode, setProductCode] = useState("");
    const [showCatalog, setShowCatalog] = useState(true);
    const [catalogSelected, setCatalogSelected] = useState(null);

    const [resolvedProduct, setResolvedProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const [selectedProduct, setSelectedProduct] = useState(null);

    const [closeEditFn, setCloseEditFn] = useState(null);


    const handleRegisterCloseEdit = useCallback((fn) => {
        setCloseEditFn(() => fn);
    }, []);

    const [wForm, setWForm] = useState({
        garantia_id: "",
        tipo: "defeito",
        prioridade: "normal",
        contato_cliente: "",
        observacoes: "",
        fotos_urls_raw: "",
    });

    const resetToInitial = async () => {
        if (closeEditFn) closeEditFn();  // ✅ fecha edição
        setSelectedProduct(null);
        setResolvedProduct(null);
        setNotFound(false);
        setProductCode("");
        setCatalogSelected(null);
        setShowCatalog(false);

        if (seller?.id) await getWarrantyProdsBySeller(seller.id);
    };

    const handleEditWarranty = (warranty) => {
        const product =
            warranty?.resolved_product ??
            null;

        if (!product?.pk) {
            console.error(
                "Garantia sem produto resolvido:",
                warranty
            );

            return;
        }

        setSelectedProduct(product);
        setResolvedProduct(product);
        setNotFound(false);

        setProductCode(
            String(
                product?.product_id_produto ??
                warranty?.product_code_real ??
                warranty?.product_code ??
                ""
            ).slice(0, 6)
        );

        setShowCatalog(false);
        setCatalogSelected(null);
    };


    const [submitState, setSubmitState] = useState({ status: "idle", message: "" });

    // ======== Fetch produtos vendidos da vendedora (Garantia) ========
    const getWarrantyProdsBySeller = async (sellerId) => {
        if (!sellerId) return;

        try {
            setIsLoadingProducts(true);

            const res = await djangoApi.get(
                `products/register_prods/${sellerId}/get_warranty_prods_by_seller/`,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            console.log('res.data', res.data)
            setLoadedProducts(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Erro ao buscar produtos de garantia", err);
            setLoadedProducts([]);
        } finally {
            setIsLoadingProducts(false);
        }
    };

    // ======== Ao trocar vendedora ========
    useEffect(() => {
        setSelectedProduct(null);
        setResolvedProduct(null);
        setNotFound(false);
        setProductCode("");
        setCatalogSelected(null);

        if (!seller?.id) {
            setLoadedProducts([]);
            return;
        }

        getWarrantyProdsBySeller(seller.id);
    }, [seller]);

    // ======== Resolver produto ao digitar 6 dígitos ========
    useEffect(() => {
        if (!seller || isLoadingProducts) return;

        if (productCode.length !== 6) {
            setResolvedProduct(null);
            setNotFound(false);
            return;
        }

        const found = findBestMatchByCode(loadedProducts, productCode);
        setResolvedProduct(found);
        setNotFound(!found);

        if (found) {
            setSelectedProduct(found);
            setCatalogSelected(null);
        }
    }, [productCode, seller, isLoadingProducts, loadedProducts]);

    // ======== Seleção via catálogo ========
    useEffect(() => {
        if (!catalogSelected) return;

        setSelectedProduct(catalogSelected);
        setResolvedProduct(catalogSelected);
        setNotFound(false);
        setProductCode(String(catalogSelected.product_id_produto || ""));
    }, [catalogSelected]);

    // ======== Pré-preencher form ao selecionar produto ========
    useEffect(() => {
        if (!selectedProduct) return;

        setWForm((prev) => ({
            ...prev,
            garantia_id: prev.garantia_id || `GAR-${Date.now().toString().slice(-6)}`,
        }));

        setSubmitState({ status: "idle", message: "" });
    }, [selectedProduct]);

    const canSearch = !!seller;

    const pageBg =
        theme.palette.mode === "dark"
            ? colors.pink[500]
            : theme.palette.background.default;

    const cardBg =
        theme.palette.mode === "dark"
            ? colors.pink[800]
            : "#ffffff";

    const subtleBorder =
        theme.palette.mode === "dark"
            ? `1px solid ${colors.grey[700]}`
            : "1px solid rgba(0,0,0,0.08)";

    const softText =
        theme.palette.mode === "dark" ? colors.grey[200] : "text.secondary";

    return (
        <Box
            sx={{
                minHeight: "100vh",
                px: 2,
                py: 3,
                bgcolor: pageBg,
                display: "flex",
                justifyContent: "center",
                paddingTop: "80px",
            }}
        >
            <Box sx={{ width: "100%" }}>
                <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, bgcolor: cardBg, border: subtleBorder }}>
                    {/* Header */}
                    <Stack spacing={0.75} sx={{ mb: 2 }} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <VerifiedUserIcon />
                            <Typography variant="h4" fontWeight={900}>
                                Garantias
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color={softText} textAlign="center">
                            Selecione a vendedora, digite o código (6 dígitos) e o produto será encontrado automaticamente.
                        </Typography>
                    </Stack>

                    {/* 1) Vendedora */}
                    <Typography variant="h3" fontWeight={900} sx={{ mb: 1 }}>
                        Cadastro de Entrada de Garantias
                    </Typography>
                    {/* 1) Vendedora */}
                    <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
                        1) Vendedora
                    </Typography>

                    <Autocomplete
                        options={SELLERS}
                        value={seller}
                        onChange={(_, v) => setSeller(v)}
                        getOptionLabel={(opt) => opt?.nome || ""}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Selecione a vendedora" />
                        )}
                    />


                    {
                        seller && !isLoadingProducts && (

                            <>
                                <Divider sx={{ my: 2 }} />

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1" fontWeight={900}>
                                        2) Buscar produto
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showCatalog}
                                                onChange={(e) => setShowCatalog(e.target.checked)}
                                                disabled={!canSearch || isLoadingProducts}
                                                color="success"
                                            />
                                        }
                                        label="Ver catálogo"
                                    />
                                </Stack>

                                <TextField
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    label="Código do produto"
                                    value={productCode}
                                    disabled={!canSearch || isLoadingProducts}
                                    onChange={(e) =>
                                        setProductCode(
                                            e.target.value
                                                .toUpperCase()                 // opcional: padroniza maiúsculo
                                                .replace(/[^A-Z0-9]/gi, "")    // remove tudo que NÃO for letra ou número
                                                .slice(0, 6)
                                        )
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Chip
                                                    size="small"
                                                    label={productCode.length === 6 ? "OK" : `${productCode.length}/6`}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />


                                {showCatalog && (
                                    <WarrantyCatalogByCategory
                                        products={loadedProducts}
                                        theme={theme}
                                        colors={colors}
                                        subtleBorder={subtleBorder}
                                        softText={softText}
                                        selectedPk={selectedProduct?.pk}
                                        onSelect={(p) => {
                                            setCatalogSelected(null); // não precisa mais
                                            setSelectedProduct(p);
                                            setResolvedProduct(p);
                                            setNotFound(false);
                                            setProductCode(String(p.product_id_produto || "").slice(0, 6));
                                        }}
                                    />
                                )}

                            </>
                        )}


                    {/* Resultado */}
                    {seller && isLoadingProducts && (
                        <>

                            <Divider sx={{ my: 2 }} />
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: subtleBorder,
                                    bgcolor: theme.palette.mode === "dark" ? colors.pink[700] : "#fafafa",
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {/* mini “thumb” */}
                                    <Box
                                        sx={{
                                            width: 86,
                                            height: 86,
                                            borderRadius: 2,
                                            border: subtleBorder,
                                            overflow: "hidden",
                                            flexShrink: 0,
                                            bgcolor: theme.palette.mode === "dark" ? colors.pink[800] : "#ffffff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "999px",
                                                border: `3px solid ${theme.palette.mode === "dark" ? colors.grey[600] : "rgba(0,0,0,0.18)"}`,
                                                borderTopColor: theme.palette.mode === "dark" ? colors.greenAccent[500] : colors.blueAccent[500],
                                                animation: "spin 0.9s linear infinite",
                                            }}
                                        />
                                    </Box>

                                    {/* texto + “progresso” */}
                                    <Stack spacing={1} sx={{ width: "100%" }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Typography fontWeight={900}>Buscando produto…</Typography>
                                            <Chip
                                                variant="outlined"
                                                label={`Vendedora: ${seller?.nome || "-"}`}
                                                sx={{ borderRadius: 2 }}
                                            />
                                            <Chip
                                                variant="outlined"
                                                label={`${loadedProducts.length} no catálogo`}
                                                sx={{ borderRadius: 2 }}
                                            />
                                        </Stack>

                                        {/* “linhas” fake */}
                                        <Box
                                            sx={{
                                                height: 10,
                                                width: "70%",
                                                borderRadius: 999,
                                                bgcolor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(0,0,0,0.08)",
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                height: 10,
                                                width: "45%",
                                                borderRadius: 999,
                                                bgcolor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(0,0,0,0.08)",
                                            }}
                                        />

                                        <Typography variant="body2" color={softText}>
                                            Aguarde um instante enquanto carregamos os produtos vendidos desta vendedora.
                                        </Typography>
                                    </Stack>
                                </Stack>

                                {/* animação */}
                                <Box
                                    sx={{
                                        "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": { transform: "rotate(360deg)" },
                                        },
                                    }}
                                />
                            </Paper>
                        </>
                    )}


                    {seller && !isLoadingProducts && productCode.length === 6 && notFound && (
                        <Alert severity="warning">
                            Produto não encontrado para este código.
                        </Alert>
                    )}

                    {resolvedProduct && (
                        <WarrantyProductCard
                            product={resolvedProduct}
                            theme={theme}
                            colors={colors}
                            subtleBorder={subtleBorder}
                            softText={softText}
                            fmtBRL={fmtBRL}
                            onRemove={resetToInitial}
                        // onRemove={() => {
                        //     setSelectedProduct(null);
                        //     setResolvedProduct(null);
                        //     setNotFound(false);
                        //     setProductCode("");
                        //     setCatalogSelected(null);
                        // }}
                        />
                    )}



                    <WarrantiesSection
                        seller={seller}
                        selectedProduct={selectedProduct}
                        theme={theme}
                        colors={colors}
                        subtleBorder={subtleBorder}
                        softText={softText}
                        fmtBRL={fmtBRL}
                        onDone={resetToInitial}
                        onEditWarranty={handleEditWarranty}   // ✅ NEW
                        onRequestCloseEdit={handleRegisterCloseEdit}
                    />


                </Paper>
            </Box>
        </Box>
    );
};

export default WarrantiesPage;
